import * as React from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import { Button, Typography } from '@swagup-com/components';
import { useMutation, useQueryClient } from 'react-query';
import mapValues from 'lodash/mapValues';
import mapKeys from 'lodash/mapKeys';
import map from 'lodash/map';
import sumBy from 'lodash/sumBy';
import isEqual from 'lodash/isEqual';
import { useParams } from 'react-router-dom';
import { moneyStr, sumByQuantity } from '../../../../../helpers/utils';
import { useOrder } from '../OrderContext';
import Drawer from '../common/Drawer';
import styles from './SendToWarehouseV2.styles';
import useStorageInfo from '../hooks/useStorageInfo';
import apiPaths from '../../../../../helpers/apiPaths';
import WarehouseCardV2 from './WarehouseCardV2';
import gtm from '../../../../../utils/gtm';

const useStyles = makeStyles(styles);

const getInitialSizes = (product, warehouseProof) =>
  product.sizes.map(s => {
    const quantityInWarehouse = warehouseProof?.sizes?.find(qw => qw.size === s.size.id)?.quantity;
    return { size: s.size.id, quantity: quantityInWarehouse ?? null };
  });

const buildWarehouseInfo = (order, storagePrices) =>
  mapValues(mapKeys(order.products, 'id'), product => {
    const warehouseProof = order.warehouseProofs.find(wp => wp.proof.id === product.id);
    const storageCategory = product.product.storage_category;

    return {
      category: storageCategory,
      sizes: getInitialSizes(product, warehouseProof),
      price: storagePrices[storageCategory]
    };
  });

const buildWarehouseProofs = warehouseInfo =>
  map(warehouseInfo, ({ sizes }, proof) => ({
    proof: Number(proof),
    sizes: sizes.filter(size => Boolean(size.quantity))
  }));

const SendToWarehouseV2 = ({ onSendQtyToWarehouse }) => {
  const classes = useStyles();

  const order = useOrder();
  const { storagePrices } = useStorageInfo();
  const [warehouseInfo, setWarehouseInfo] = React.useState(() => buildWarehouseInfo(order, storagePrices));

  const { id } = useParams();
  const queryClient = useQueryClient();

  const total = sumBy(Object.values(warehouseInfo), info => sumByQuantity(info.sizes));
  const price = sumBy(Object.values(warehouseInfo), info => sumByQuantity(info.sizes) * info.price);

  const quantitiesChanged = order.products.some(
    p =>
      !isEqual(
        warehouseInfo[p.id].sizes.filter(sz => Boolean(sz.quantity)),
        order.warehouseProofs.find(wp => wp.proof.id === p.id)?.sizes || []
      )
  );

  const warehouseMutation = useMutation(apiPaths.warehouseProofs(order.id), {
    onSuccess: () => {
      queryClient.invalidateQueries(apiPaths.warehouseProofs(order.id));
      queryClient.invalidateQueries(apiPaths.opportunities);
      onSendQtyToWarehouse({ quantitiesChanged });
    }
  });

  const warehouseMutationPayload = React.useMemo(() => ({ id, proofs: buildWarehouseProofs(warehouseInfo) }), [
    id,
    warehouseInfo
  ]);
  const handleSubmit = () => warehouseMutation.mutate(warehouseMutationPayload);

  const handleQuantitiesChange = product => info => {
    setWarehouseInfo(prevInfo => ({
      ...prevInfo,
      [product.id]: { ...prevInfo[product.id], ...info }
    }));
  };

  const disabled = warehouseMutation.isLoading || !quantitiesChanged;

  return (
    <>
      <Grid className={classes.title}>
        <Typography variant="h5SemiBoldInter">Your SwagUp Storage</Typography>
      </Grid>
      <Grid container alignItems="flex-end" justifyContent="space-between">
        <Grid item xs>
          <Typography variant="body2RegularInter" style={{ color: '#989EA4' }}>
            Total Quantity
          </Typography>
          <Typography variant="h6SemiBoldInter" style={{ marginTop: 8, marginBottom: 8 }}>
            {total}
          </Typography>
        </Grid>
        <Grid item xs style={{ textAlign: 'end' }}>
          <Typography variant="body2RegularInter" style={{ color: '#989EA4' }}>
            Total Storage Cost
          </Typography>
          <Typography variant="h6SemiBoldInter" style={{ marginTop: 8, marginBottom: 8 }}>
            {moneyStr(price)}
          </Typography>
        </Grid>
      </Grid>
      <Typography variant="body3RegularInter" className={classes.detail}>
        Your swag is automatically stored to SwagUp Warehouse at various pricing.
        <Typography variant="body3UnderlineInter" className={classes.link}>
          <a href="https://www.swagup.com/pricing" target="_blank" rel="noreferrer">
            View Details
          </a>
        </Typography>
      </Typography>
      <Grid container direction="column" alignItems="center">
        {order.products.map(product => (
          <WarehouseCardV2
            key={product.id}
            product={product}
            warehouseInfo={warehouseInfo[product.id]}
            onSizesChange={handleQuantitiesChange(product)}
          />
        ))}
      </Grid>
      <Grid container style={{ marginTop: 32 }}>
        <Button
          variant="primary"
          onClick={() => {
            gtm.onClick('Store Selected items in Warehouse');
            handleSubmit();
          }}
          loading={warehouseMutation.isLoading}
          disabled={disabled}
          className={classes.button}
        >
          <Typography variant="buttonMediumInter" style={{ color: '#FFFFFF' }}>
            Save Changes
          </Typography>
        </Button>
      </Grid>
    </>
  );
};

const Wrapper = ({ open, onClose, onSendQtyToWarehouse }) => {
  const classes = useStyles();

  return (
    <Drawer open={open} onClose={onClose} classes={{ paper: classes.drawerPaper }}>
      <SendToWarehouseV2 onSendQtyToWarehouse={onSendQtyToWarehouse} />
    </Drawer>
  );
};
export default Wrapper;
